import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { memberApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  DevelopPointTargetsResponse,
  SaveDevelopPointTargetInput,
} from '../types/develop-point-target.js';
import type {
  MemberListParams,
  MemberTrendParams,
  MemberDetailParams,
  MemberGithubActivityParams,
  MemberGithubActivityIndexParams,
} from '../types/api.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー一覧をTanStack Query経由で取得。組織フィルタ指定で所属メンバーのリストを返す
 * @graph-connects /members [calls] メンバー一覧取得API
 * @graph-connects memberApi.getMembers [calls] メンバー一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberList(params?: MemberListParams) {
  return useQuery({
    queryKey: queryKeys.members.list(params),
    queryFn: () => memberApi.getMembers(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー詳細をTanStack Query経由で取得。メール指定で個人のKPI集計・所属情報を返す
 * @graph-connects /members/:email [calls] メンバー詳細取得API
 * @graph-connects memberApi.getMemberDetail [calls] メンバー詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberDetail(email: string | null) {
  return useQuery({
    queryKey: queryKeys.members.detail(email ?? ''),
    queryFn: () => memberApi.getMemberDetail(email ?? ''),
    enabled: email !== null && email !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバートレンドをTanStack Query経由で取得。メール・期間指定で個人の日次KPI推移データを返す
 * @graph-connects /members/:email/trend [calls] メンバートレンド取得API
 * @graph-connects memberApi.getMemberTrend [calls] メンバートレンドAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberTrend(email: string | null, params?: MemberTrendParams) {
  return useQuery({
    queryKey: queryKeys.members.trend(email ?? '', params),
    queryFn: () => memberApi.getMemberTrend(email ?? '', params),
    enabled: email !== null && email !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーの開発ポイント詳細をTanStack Query経由で取得。メール・日付範囲で個人の開発ポイント一覧を返す
 * @graph-connects /members/:email/develop-points [calls] メンバー開発ポイント詳細API
 * @graph-connects memberApi.getMemberDevelopPoints [calls] メンバー開発ポイントAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberDevelopPoints(email: string | null, params: MemberDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.members.developPoints(email ?? '', params ?? undefined),
    queryFn: () => memberApi.getMemberDevelopPoints(email ?? '', params ?? undefined),
    enabled: email !== null && email !== '' && params !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーのリリース詳細をTanStack Query経由で取得。メール・日付範囲で個人のリリース一覧を返す
 * @graph-connects /members/:email/releases [calls] メンバーリリース詳細API
 * @graph-connects memberApi.getMemberReleases [calls] メンバーリリースAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberReleases(email: string | null, params: MemberDetailParams | null) {
  return useQuery({
    queryKey: queryKeys.members.releases(email ?? '', params ?? undefined),
    queryFn: () => memberApi.getMemberReleases(email ?? '', params ?? undefined),
    enabled: email !== null && email !== '' && params !== null,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー四半期KPIをTanStack Query経由で取得。メール・四半期指定で月別の目標/実績/達成率を返す。
 *  四半期切替時のちらつき防止に keepPreviousData を使用
 * @graph-connects /members/:email/quarterly-kpi [calls] メンバー四半期KPI取得API
 * @graph-connects memberApi.getMemberQuarterlyKpi [calls] メンバー四半期KPI APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberQuarterlyKpi(email: string | null, quarter?: string) {
  return useQuery({
    queryKey: queryKeys.members.quarterlyKpi(email ?? '', quarter),
    queryFn: () =>
      memberApi.getMemberQuarterlyKpi(email ?? '', quarter !== undefined ? { quarter } : undefined),
    enabled: email !== null && email !== '',
    placeholderData: keepPreviousData,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーの処理中pt明細（子チケット単位）をTanStack Query経由で取得。
 *  当月ptカードのドリルダウンモーダル用。enabled=false で開くまで取得を遅延できる
 * @graph-connects /members/:email/wip-points [calls] メンバー処理中pt明細API
 * @graph-connects memberApi.getMemberWipPoints [calls] メンバー処理中pt明細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberWipPoints(email: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.members.wipPoints(email ?? ''),
    queryFn: () => memberApi.getMemberWipPoints(email ?? ''),
    enabled: enabled && email !== null && email !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーの未対応pt明細（子チケット単位・予定pt）をTanStack Query経由で取得。
 *  未対応ptカードの大数表示とドリルダウンモーダル用
 * @graph-connects /members/:email/open-points [calls] メンバー未対応pt明細API
 * @graph-connects memberApi.getMemberOpenPoints [calls] メンバー未対応pt明細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberOpenPoints(email: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.members.openPoints(email ?? ''),
    queryFn: () => memberApi.getMemberOpenPoints(email ?? ''),
    enabled: enabled && email !== null && email !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人別GitHub activity summaryをTanStack Query経由で取得
 * @graph-connects /members/:login/github/summary [calls] GitHub activity summary API
 */
export function useMemberGithubSummary(login: string | null, params?: MemberGithubActivityParams) {
  return useQuery({
    queryKey: queryKeys.members.githubSummary(login ?? '', params),
    queryFn: () => memberApi.getGithubSummary(login ?? '', params),
    enabled: login !== null && login !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人別GitHub activity timeseriesをTanStack Query経由で取得
 * @graph-connects /members/:login/github/timeseries [calls] GitHub activity timeseries API
 */
export function useMemberGithubTimeseries(
  login: string | null,
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubTimeseries(login ?? '', params),
    queryFn: () => memberApi.getGithubTimeseries(login ?? '', params),
    enabled: login !== null && login !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人別GitHub activity repo別内訳をTanStack Query経由で取得
 * @graph-connects /members/:login/github/by-repo [calls] GitHub activity by-repo API
 */
export function useMemberGithubByRepo(login: string | null, params?: MemberGithubActivityParams) {
  return useQuery({
    queryKey: queryKeys.members.githubByRepo(login ?? '', params),
    queryFn: () => memberApi.getGithubByRepo(login ?? '', params),
    enabled: login !== null && login !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人別GitHub activity partner rankingをTanStack Query経由で取得
 * @graph-connects /members/:login/github/partners [calls] GitHub activity partners API
 */
export function useMemberGithubPartners(login: string | null, params?: MemberGithubActivityParams) {
  return useQuery({
    queryKey: queryKeys.members.githubPartners(login ?? '', params),
    queryFn: () => memberApi.getGithubPartners(login ?? '', params),
    enabled: login !== null && login !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人別GitHub activity logをTanStack Query経由で取得
 * @graph-connects /members/:login/github/activities [calls] GitHub activity log API
 */
export function useMemberGithubActivities(
  login: string | null,
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubActivities(login ?? '', params),
    queryFn: () => memberApi.getGithubActivities(login ?? '', params),
    enabled: login !== null && login !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business GitHub activity横断indexをTanStack Query経由で取得
 * @graph-connects /members/github/index [calls] GitHub activity index API
 */
export function useMemberGithubIndex(params?: MemberGithubActivityIndexParams) {
  return useQuery({
    queryKey: queryKeys.members.githubIndex(params),
    queryFn: () => memberApi.getGithubIndex(params),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム集計 GitHub activity summary を取得。logins 空の場合は fetch しない
 * @graph-connects /members/github/aggregate/summary [calls] aggregate summary API
 */
export function useMemberGithubAggregateSummary(
  logins: string[],
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubAggregateSummary(logins, params),
    queryFn: () => memberApi.getGithubAggregateSummary(logins, params),
    enabled: logins.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム集計 GitHub activity timeseries を取得
 * @graph-connects /members/github/aggregate/timeseries [calls] aggregate timeseries API
 */
export function useMemberGithubAggregateTimeseries(
  logins: string[],
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubAggregateTimeseries(logins, params),
    queryFn: () => memberApi.getGithubAggregateTimeseries(logins, params),
    enabled: logins.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム集計 GitHub activity by-repo を取得
 * @graph-connects /members/github/aggregate/by-repo [calls] aggregate by-repo API
 */
export function useMemberGithubAggregateByRepo(
  logins: string[],
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubAggregateByRepo(logins, params),
    queryFn: () => memberApi.getGithubAggregateByRepo(logins, params),
    enabled: logins.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム集計 GitHub activity partners を取得
 * @graph-connects /members/github/aggregate/partners [calls] aggregate partners API
 */
export function useMemberGithubAggregatePartners(
  logins: string[],
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubAggregatePartners(logins, params),
    queryFn: () => memberApi.getGithubAggregatePartners(logins, params),
    enabled: logins.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全体 / チーム集計 GitHub activity 最近の動き を取得
 * @graph-connects /members/github/aggregate/activities [calls] aggregate activity log API
 */
export function useMemberGithubAggregateActivities(
  logins: string[],
  params?: MemberGithubActivityParams,
) {
  return useQuery({
    queryKey: queryKeys.members.githubAggregateActivities(logins, params),
    queryFn: () => memberApi.getGithubAggregateActivities(logins, params),
    enabled: logins.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーが自分で設定した開発pt 目標線（目標pt・期間・色）の一覧をTanStack Query経由で取得。日進ビューの追加目標線の描画に使う
 * @graph-connects /members/:email/develop-point-targets [calls] 自己設定目標線の一覧取得API
 * @graph-connects memberApi.getMemberDevelopPointTargets [calls] 自己設定目標線APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMemberDevelopPointTargets(email: string | null) {
  return useQuery({
    queryKey: queryKeys.members.developPointTargets(email ?? ''),
    queryFn: () => memberApi.getMemberDevelopPointTargets(email ?? ''),
    enabled: email !== null && email !== '',
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発pt 自己設定目標線の追加・編集ミューテーション。PUT のレスポンス（最新の全件）でキャッシュを置き換える。期間が重なる場合は API が 409 を返し、既存の目標線は変更されない
 * @graph-connects /members/:email/develop-point-targets [calls] 自己設定目標線の追加・編集API（PUT）
 * @graph-connects memberApi.saveMemberDevelopPointTarget [calls] 自己設定目標線保存APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSaveMemberDevelopPointTarget(email: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveDevelopPointTargetInput) =>
      memberApi.saveMemberDevelopPointTarget(email, input),
    onSuccess: (updated: DevelopPointTargetsResponse) => {
      queryClient.setQueryData(queryKeys.members.developPointTargets(email), updated);
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発pt 自己設定目標線を 1 本だけ解除するミューテーション。DELETE のレスポンス（残りの全件）でキャッシュを置き換える。API 側で本人以外は 403 になる
 * @graph-connects /members/:email/develop-point-targets/:id [calls] 自己設定目標線の解除API（DELETE）
 * @graph-connects memberApi.deleteMemberDevelopPointTarget [calls] 自己設定目標線解除APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useDeleteMemberDevelopPointTarget(email: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => memberApi.deleteMemberDevelopPointTarget(email, id),
    onSuccess: (updated: DevelopPointTargetsResponse) => {
      queryClient.setQueryData(queryKeys.members.developPointTargets(email), updated);
    },
  });
}
